/**
 * Временные id новой публикации → настоящие (09.10.2026).
 *
 * Новая публикация уходит на сервер через 5 секунд («Отменить»). До этого у
 * неё id `new-…`, а у роликов — свои временные. Монтажёр ставил себе
 * публикацию, сразу открывал и кидал видео — загрузка уходила с `new-…`,
 * сервер отвечал «Задача не найдена», и публикацию заводили второй раз.
 *
 * Теперь любой запрос «Монтажа» с временным id сперва досылает создание,
 * ждёт ответа и идёт уже с настоящими id задачи и ролика.
 */

const taskIds = new Map<string, string>();
const itemIds = new Map<string, string>();
const inFlight = new Map<string, Promise<void>>();

/** Стор подставляет сюда «отправить создание сейчас» — без циклического импорта. */
let flushCreate: (tempId: string) => Promise<void> = async () => {};
export function bindFlushCreate(fn: (tempId: string) => Promise<void>) {
  flushCreate = fn;
}

export const isTemp = (id: string | undefined | null): id is string => Boolean(id && id.startsWith("new-"));

/** Создание ушло: запомнить, чтобы параллельные запросы ждали его, а не слали второй раз. */
export function trackCreate(tempId: string, promise: Promise<void>) {
  inFlight.set(tempId, promise);
  void promise.finally(() => inFlight.delete(tempId));
}

/** Сервер ответил: временные id задачи и роликов (по порядку) → настоящие. */
type SettleListener = (tempId: string, realId: string, items: { temp: string; real: string }[]) => void;
const listeners: SettleListener[] = [];
/** Кому важно узнать настоящие id — например, фоновым загрузкам. */
export function onSettle(fn: SettleListener) {
  listeners.push(fn);
}

export function settle(tempId: string, realId: string, items: { temp: string; real: string }[]) {
  taskIds.set(tempId, realId);
  for (const { temp, real } of items) itemIds.set(temp, real);
  for (const fn of listeners) fn(tempId, realId, items);
}

export async function realTaskId(id: string): Promise<string> {
  if (!isTemp(id)) return id;
  if (!taskIds.has(id)) {
    const running = inFlight.get(id);
    await (running ?? flushCreate(id));
  }
  return taskIds.get(id) ?? id;
}

export const realItemId = (id: string) => itemIds.get(id) ?? id;

/** `/montage/tasks/new-…/items/<временный>/…` → с настоящими id. */
export async function realPath(path: string): Promise<string> {
  const m = path.match(/^\/montage\/tasks\/(new-[^/?]+)/);
  let out = path;
  if (m) out = out.replace(m[1], await realTaskId(m[1]));
  return out.replace(/\/items\/([^/?]+)/, (all, item: string) => `/items/${realItemId(item)}`);
}
